import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Generador360.css';

const MODOS = [
  {
    id: '360',
    label: 'Panorama 360°',
    icono: '🌐',
    descripcion: 'Genera una imagen equirrectangular lista para usarla como escena del recorrido virtual.',
    modelo: 'orbin-ahmed/360_pano',
    placeholder: 'Ej: Vestíbulo de centro comercial moderno, suelo de mármol blanco, plantas, luz natural suave, techo alto con claraboya, estilo fotorrealista',
    tipoSubida: 'panorama',
    nombreDefecto: 'Escena generada por IA',
  },
  {
    id: 'imagen',
    label: 'Imagen normal',
    icono: '🖼️',
    descripcion: 'Genera cualquier imagen estándar: logos, personas, vallas publicitarias, fondos para hotspots.',
    modelo: 'black-forest-labs/flux-schnell',
    placeholder: 'Ej: Logotipo minimalista de tienda de ropa, fondo blanco limpio, letras sans-serif azul marino',
    tipoSubida: 'imagen',
    nombreDefecto: 'Imagen generada por IA',
  },
];

const Generador360 = () => {
  const navigate = useNavigate();
  const [modo, setModo] = useState('360');
  const [prompt, setPrompt] = useState('');
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);
  const [centros, setCentros] = useState([]);
  const [centroId, setCentroId] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [escuchando, setEscuchando] = useState(false);
  const recognitionRef = useRef(null);

  const modoActual = MODOS.find(m => m.id === modo) || MODOS[0];

  const cargarCentros = () => {
    api.get('/api/centros').then(r => {
      if (r.data.success) setCentros(r.data.centros || []);
    }).catch(() => setCentros([]));
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin'); return; }
    cargarCentros();
  }, [navigate]);

  useEffect(() => { if (resultado) cargarCentros(); }, [resultado]);

  // Al cambiar de modo limpiar resultado anterior
  const cambiarModo = (id) => {
    setModo(id);
    setResultado(null);
    setError('');
  };

  /* ── voz ── */
  const iniciarVoz = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Tu navegador no soporta voz. Usa Chrome o Edge.');
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'es-ES';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onstart  = () => setEscuchando(true);
    rec.onend    = () => setEscuchando(false);
    rec.onerror  = () => setEscuchando(false);
    rec.onresult = e => {
      const texto = e.results[0][0].transcript;
      setPrompt(prev => prev ? prev + ' ' + texto : texto);
    };
    recognitionRef.current = rec;
    rec.start();
  };

  const detenerVoz = () => {
    recognitionRef.current?.stop();
    setEscuchando(false);
  };

  /* ── generar ── */
  const generar = async () => {
    const texto = prompt.trim();
    if (!texto) { setError('Escribe o di en voz alta la descripción.'); return; }
    setError('');
    setResultado(null);
    setGenerando(true);
    try {
      const res = await api.post('/api/generar-360', { prompt: texto, tipo: modo });
      if (res.data.success && res.data.imageUrl) {
        setResultado({ imageUrl: res.data.imageUrl, thumbUrl: res.data.thumbUrl, tipo: modo });
      } else {
        setError(res.data?.message || 'No se pudo generar la imagen.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al generar.');
    } finally {
      setGenerando(false);
    }
  };

  /* ── añadir al centro ── */
  const añadirACentro = async () => {
    if (!resultado?.imageUrl || !centroId) { setError('Elige un centro comercial.'); return; }
    setSubiendo(true);
    setError('');
    try {
      const blobRes = await fetch(resultado.imageUrl, { mode: 'cors' });
      const blob = await blobRes.blob();
      const ext = blob.type.includes('png') ? 'png' : 'jpg';
      const formData = new FormData();
      formData.append('imagen', blob, `generado-ia.${ext}`);
      formData.append('centro_id', centroId);
      formData.append('nombre', modoActual.nombreDefecto);
      formData.append('tipo', resultado.tipo === '360' ? 'pasillo' : 'imagen');
      await api.post('/api/panoramas', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResultado(null);
      setPrompt('');
      setCentroId('');
      alert('Imagen añadida al centro. Puedes usarla en el Constructor.');
      navigate('/admin/constructor');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al subir.');
    } finally {
      setSubiendo(false);
    }
  };

  /* ── render ── */
  return (
    <div className="generador360">
      <header className="generador360-header">
        <h1>Crear imágenes con IA</h1>
        <div className="generador360-links">
          <a href="/admin/panel">Panel</a>
          <a href="/admin/constructor">Constructor</a>
        </div>
      </header>

      <main className="generador360-main">

        {/* Selector de modo */}
        <div className="gen-modos">
          {MODOS.map(m => (
            <button
              key={m.id}
              type="button"
              className={'gen-modo-btn' + (modo === m.id ? ' activo' : '')}
              onClick={() => cambiarModo(m.id)}
              disabled={generando}
            >
              <span className="gen-modo-icono">{m.icono}</span>
              <span className="gen-modo-label">{m.label}</span>
            </button>
          ))}
        </div>

        {/* Info del modo */}
        <div className="gen-modo-info">
          <p>{modoActual.descripcion}</p>
          <span className="gen-modelo-badge">Modelo: {modoActual.modelo}</span>
        </div>

        {/* Aviso precio */}
        <div className="gen-aviso-precio">
          <strong>⚡ Requiere <code>REPLICATE_API_TOKEN</code> en el <code>.env</code> del servidor.</strong>
          {' '}Coste aproximado:{' '}
          {modo === '360'
            ? 'orbin-ahmed/360_pano ~0.03 € por imagen.'
            : 'flux-schnell ~0.003 € por imagen (muy económico).'}
          {' '}Obtén tu token en{' '}
          <a href="https://replicate.com/account/api-tokens" target="_blank" rel="noopener noreferrer">
            replicate.com
          </a>.
        </div>

        {/* Prompt */}
        <div className="generador360-input-wrap">
          <label htmlFor="gen-prompt">Describe lo que quieres generar</label>
          <textarea
            id="gen-prompt"
            className="generador360-textarea"
            placeholder={modoActual.placeholder}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={4}
            disabled={generando}
          />
          <div className="generador360-buttons">
            <button
              type="button"
              className="btn-generar-principal"
              onClick={generar}
              disabled={generando || !prompt.trim()}
            >
              {generando
                ? (modo === '360' ? 'Generando panorama…' : 'Generando imagen…')
                : (modo === '360' ? '🌐 Generar panorama 360°' : '🖼️ Generar imagen')}
            </button>
            <button
              type="button"
              className={'btn-voz' + (escuchando ? ' activo' : '')}
              onClick={escuchando ? detenerVoz : iniciarVoz}
              disabled={generando}
              title="Dictar descripción por voz"
            >
              {escuchando ? '⏹ Detener voz' : '🎤 Voz'}
            </button>
          </div>
        </div>

        {error && <div className="generador360-error">{error}</div>}

        {generando && (
          <div className="generador360-progress">
            <div className="spinner" />
            <p>
              {modo === '360'
                ? 'Generando panorama 360° (puede tardar 30–60 seg)…'
                : 'Generando imagen (unos segundos)…'}
            </p>
            <p className="gen-progress-sub">No cierres esta página.</p>
          </div>
        )}

        {resultado && !generando && (
          <div className="generador360-resultado">
            <h3>
              {resultado.tipo === '360' ? '🌐 Panorama 360° generado' : '🖼️ Imagen generada'}
            </h3>
            <div className="generador360-preview">
              <img
                src={resultado.thumbUrl || resultado.imageUrl}
                alt="Imagen generada por IA"
                style={resultado.tipo === '360' ? { aspectRatio: '2/1', objectFit: 'cover' } : {}}
              />
            </div>
            <a
              href={resultado.imageUrl}
              download="generado-ia.jpg"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-descargar"
            >
              ⬇ Descargar imagen
            </a>

            <div className="generador360-añadir">
              <label>
                {resultado.tipo === '360'
                  ? 'Añadir como panorama a mi centro:'
                  : 'Añadir a mis recursos (panoramas/imágenes):'}
              </label>
              {centros.length === 0 ? (
                <p className="generador360-sin-centros">
                  No hay centros. Crea uno en el{' '}
                  <a href="/admin/panel">Panel</a> y vuelve aquí.
                </p>
              ) : (
                <>
                  <select value={centroId} onChange={e => setCentroId(e.target.value)}>
                    <option value="">Elige un centro</option>
                    {centros.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-añadir"
                    onClick={añadirACentro}
                    disabled={subiendo || !centroId}
                  >
                    {subiendo ? 'Subiendo…' : '+ Añadir al centro'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Generador360;
